import {
  Backdrop,
  Box,
  CircularProgress,
  CssBaseline,
  ThemeProvider,
  Typography,
} from "@mui/material";
import { useMemo } from "react";
import { createTheme } from "@mui/material";
import { useSelector } from "react-redux";
import { themeSettings } from "theme";
import {
  BrowserRouter as Router,
  Navigate,
  Route,
  Routes,
} from "react-router-dom";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import Dashboard from "scenes/workspace/dashboard/Dashboard";
import Layout from "scenes/layout/Layout";
import Services from "scenes/services/Services";
import ServiceProfile from "scenes/services/ServiceProfile";
import Interpretations from "scenes/services/interpretations/Interpretations";
import Clients from "scenes/connections/clients/Clients";
import Linguists from "scenes/connections/linguists/Linguists";
import { useStateContext } from "contexts/ContextProvider";
import { useAuth } from "contexts/AuthContext";
import { WebSocketProvider } from "contexts/WebSocketContext";
import { PresenceProvider } from "contexts/PresenceContext";
import ClientNewEdit from "scenes/connections/clients/ClientNewEdit";
import ClientProfile from "scenes/connections/clients/ClientProfile";
import LinguistsProfile from "scenes/connections/linguists/LinguistProfile";
import { OVERLAY_NAMES } from "constants/Overlays";
import RequestorNewEdit from "scenes/connections/clients/RequestorNewEdit";
import RequestorMergeOverlay from "scenes/connections/clients/RequestorMergeOverlay";
import ContactPersonNewEdit from "scenes/connections/clients/ContactPersonNewEdit";
import ContactPersonMergeOverlay from "scenes/connections/clients/ContactPersonMergeOverlay";
import ClientMergeOverlay from "scenes/connections/clients/ClientMergeOverlay";
import LinguistNewEdit from "scenes/connections/linguists/LinguistNewEdit";
import LinguistMergeOverlay from "scenes/connections/linguists/LinguistMergeOverlay";
import LinguistAvailabilityEdit from "scenes/connections/linguists/LinguistAvailabilityEdit";
import LinguistRateUpdate from "scenes/connections/linguists/LinguistRateUpdate";
import LinguistDocumentGeneration from "scenes/connections/linguists/LinguistDocumentGeneration";
import LinguistsGeneratedDocuments from "scenes/connections/linguists/LinguistsGeneratedDocuments";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import InterpretationProfile from "scenes/services/interpretations/InterpretationProfile";
import ServiceNoteNewEdit from "scenes/services/ServiceNoteNewEdit";
import ClientNoteNewEdit from "scenes/connections/clients/ClientNoteNewEdit";
import LinguistNoteNewEdit from "scenes/connections/linguists/LinguistNoteNewEdit";
import TaskNewEdit from "scenes/workspace/tasks/TaskNewEdit";
import TaskProfile from "scenes/workspace/tasks/TaskProfile";
import TasksAutomated from "scenes/workspace/tasks/TasksAutomated";
import InterpretationNew from "scenes/services/interpretations/InterpretationNewEdit";
import InterpretationPreAssign from "scenes/services/interpretations/InterpretationPreAssign";
import InterpretationSendDetails from "scenes/services/interpretations/InterpretationSendDetails";
import InterpretationReminders from "scenes/services/interpretations/InterpretationReminders";
import Translations from "scenes/services/translations/Translations";
import TranslationProfile from "scenes/services/translations/TranslationProfile";
import TranslationNewEdit from "scenes/services/translations/TranslationNewEdit";
import TranslationDocuments from "scenes/services/translations/TranslationDocuments";
import OtherServiceNewEdit from "scenes/services/other/OtherServiceNewEdit";
import OtherServices from "scenes/services/other/OtherServices";
import OtherServiceProfile from "scenes/services/other/OtherServiceProfile";
import Login from "scenes/login/Login";
import LoginConfirmation from "scenes/login/LoginConfirmation";
import LinguistLogin from "scenes/linguistPortal/LinguistLogin";
import LinguistDashboard from "scenes/linguistPortal/LinguistDashboard";
import LinguistAssignmentDetail from "scenes/linguistPortal/LinguistAssignmentDetail";
import LinguistProfile from "scenes/linguistPortal/LinguistProfile";
import LinguistNotifications from "scenes/linguistPortal/LinguistNotifications";
import LinguistInvitationSetup from "scenes/linguistPortal/LinguistInvitationSetup";
import LinguistPortalLayout from "scenes/linguistPortal/LinguistPortalLayout";
import LinguistInterpretations from "scenes/linguistPortal/LinguistInterpretations";
import LinguistProfileEditOverlay from "scenes/linguistPortal/LinguistProfileEditOverlay";
import LinguistNotificationSettingsEditOverlay from "scenes/linguistPortal/LinguistNotificationSettingsEditOverlay";
import Expenses from "scenes/management/expenses/Expenses";
import Invoices from "scenes/management/invoices/Invoices";
import LinguistsPayments from "scenes/management/linguistsPayments/LinguistsPayments";
import InvoiceExport from "scenes/management/invoices/InvoiceExport";
import Members from "scenes/settings/members/Members";
import MemberNewEdit from "scenes/settings/members/MemberNewEdit";
import MemberProfile from "scenes/settings/members/MemberProfile";
import Settings from "scenes/settings/Settings";
import SettingsProfileEdit from "scenes/settings/SettingsProfileEdit";
import LanguageNewEdit from "scenes/settings/LanguageNewEdit";
import DialectNewEdit from "scenes/settings/DialectNewEdit";
import CommunicationTemplateNewEdit from "scenes/settings/CommunicationTemplateNewEdit";
import MileageRateNewEdit from "scenes/settings/MileageRateNewEdit";
import UnitNewEdit from "scenes/settings/UnitNewEdit";
import HolidayNewEdit from "scenes/settings/HolidayNewEdit";
import LinguistRequirementNewEdit from "scenes/settings/LinguistRequirementNewEdit";
import DocumentTemplateNewEdit from "scenes/settings/DocumentTemplateNewEdit";
import ServiceDocumentNewEdit from "scenes/settings/ServiceDocumentNewEdit";
import PaymentMethodNewEdit from "scenes/settings/PaymentMethodNewEdit";
import ServiceTypeNewEdit from "scenes/settings/ServiceTypeNewEdit";
import ServiceCostTypeNewEdit from "scenes/settings/ServiceCostTypeNewEdit";
import ServiceCostTypeRulesEdit from "scenes/settings/ServiceCostTypeRulesEdit";
import NotificationNewEdit from "scenes/settings/NotificationNewEdit";
import TimesheetsManualNewEdit from "scenes/settings/TimesheetsManualNew";
import TimesheetsImport from "scenes/settings/TimesheetsImport";
import ExpensesCategoryNewEdit from "scenes/settings/ExpensesCategoryNewEdit";
import ExpensesSubcategoryNewEdit from "scenes/settings/ExpensesSubcategoryNewEdit";
import ExpenseNewEdit from "scenes/management/expenses/ExpenseNewEdit";
import ExpenseProfile from "scenes/management/expenses/ExpenseProfile";
import Reports from "scenes/management/reports/Reports";
import AIInsightsViewOverlay from "scenes/management/reports/AIInsightsViewOverlay";
import ChangeCategory from "scenes/services/ChangeCategory";
import Help from "scenes/help/Help";
import InterpretationOPIUpload from "scenes/services/interpretations/InterpretationOPIUpload";
import RateNewEdit from "scenes/connections/clients/RateNewEdit";
import InvoiceProfile from "scenes/management/invoices/InvoiceProfile";
import InvoiceNew from "scenes/management/invoices/InvoiceNew";
import InvoiceEdit from "scenes/management/invoices/InvoiceEdit";
import BugReportNew from "scenes/bugReports/BugReportNew";
import BugReports from "scenes/bugReports/BugReports";
import BugReportProfile from "scenes/bugReports/BugReportProfile";
import OperationsTools from "scenes/tools/OperationsTools";
import Calendar from "scenes/workspace/calendar/Calendar";
import Tasks from "scenes/workspace/tasks/Tasks";
import Notifications from "scenes/notifications/Notifications";
import GlobalSearch from "scenes/search/GlobalSearch";
import NotFound from "scenes/notFound/NotFound";
import ProtectedRoute from "components/ProtectedRoute";
import LinguistProtectedRoute from "components/LinguistProtectedRoute";
import AmerCard from "scenes/public/digitalBusinessCards/AmerCard";
import OperationsCard from "scenes/public/digitalBusinessCards/OperationsCard";
import CardsRedirect from "components/CardsRedirect";
import { isCardsSubdomain } from "utils/subdomainDetector";
import LinguistAssignmentFinalize from "scenes/linguistPortal/LinguistAssignmentFinalize";

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.tz.setDefault("America/New_York");

function App() {
  const mode = useSelector((state) => state.global.mode);
  const theme = useMemo(() => createTheme(themeSettings(mode)), [mode]);

  const { overlayName, overlayData } = useStateContext();
  const { currentUser } = useAuth();
  
  // Detect if we're on the cards subdomain
  const isCards = isCardsSubdomain();

  const renderOverlay = () => {
    switch (overlayName) {
      case OVERLAY_NAMES.CLIENT_NEW_EDIT:
        return <ClientNewEdit />;
      case OVERLAY_NAMES.REQUESTOR_NEW_EDIT:
        return <RequestorNewEdit />;
      case OVERLAY_NAMES.CONTACT_PERSON_NEW_EDIT:
        return <ContactPersonNewEdit />;
      case OVERLAY_NAMES.CONTACT_PERSON_MERGE:
        return <ContactPersonMergeOverlay />;
      case OVERLAY_NAMES.REQUESTOR_MERGE:
        return <RequestorMergeOverlay />;
      case OVERLAY_NAMES.CLIENT_MERGE:
        return <ClientMergeOverlay />;
      case OVERLAY_NAMES.LINGUIST_NEW_EDIT:
        return <LinguistNewEdit />;
      case OVERLAY_NAMES.LINGUIST_MERGE:
        return <LinguistMergeOverlay />;
      case OVERLAY_NAMES.LINGUIST_AVAILABILITY_EDIT:
        return <LinguistAvailabilityEdit />;
      case OVERLAY_NAMES.LINGUIST_RATE_UPDATE:
        return <LinguistRateUpdate />;
      case OVERLAY_NAMES.LINGUIST_DOCUMENT_GENERATION:
        return <LinguistDocumentGeneration />;
      case OVERLAY_NAMES.LINGUISTS_GENERATED_DOCUMENTS:
        return <LinguistsGeneratedDocuments />;
      case OVERLAY_NAMES.LINGUIST_PORTAL_PROFILE_EDIT:
        return <LinguistProfileEditOverlay />;
      case OVERLAY_NAMES.LINGUIST_PORTAL_NOTIFICATION_SETTINGS_EDIT:
        return <LinguistNotificationSettingsEditOverlay />;
      case OVERLAY_NAMES.SERVICE_NOTE_NEW_EDIT:
        return <ServiceNoteNewEdit />;
      case OVERLAY_NAMES.CLIENT_NOTE_NEW_EDIT:
        return <ClientNoteNewEdit />;
      case OVERLAY_NAMES.LINGUIST_NOTE_NEW_EDIT:
        return <LinguistNoteNewEdit />;
      case OVERLAY_NAMES.INTERPRETATION_NEW_EDIT:
        return <InterpretationNew />;
      case OVERLAY_NAMES.INTERPRETATION_PRE_ASSIGN:
        return <InterpretationPreAssign />;
      case OVERLAY_NAMES.INTERPRETATION_OPI_UPLOAD:
        return <InterpretationOPIUpload />;
      case OVERLAY_NAMES.INTERPRETATION_SEND_DETAILS:
        return <InterpretationSendDetails />;
      case OVERLAY_NAMES.INTERPRETATION_REMINDERS:
        return <InterpretationReminders />;
      case OVERLAY_NAMES.TRANSLATION_NEW_EDIT:
        return <TranslationNewEdit />;
      case OVERLAY_NAMES.TRANSLATION_DOCUMENTS:
        return <TranslationDocuments />;
      case OVERLAY_NAMES.OTHERSERVICE_NEW_EDIT:
        return <OtherServiceNewEdit />;
      case OVERLAY_NAMES.SERVICE_CHANGE_CATEGORY:
        return <ChangeCategory />;
      case OVERLAY_NAMES.RATE_NEW_EDIT:
        return <RateNewEdit />;
      case OVERLAY_NAMES.INVOICES_NEW_EDIT:
        return <InvoiceNew />;
      case OVERLAY_NAMES.INVOICES_ACTION_EDIT:
        return <InvoiceEdit />;
      case OVERLAY_NAMES.INVOICES_EXPORT:
        return <InvoiceExport />;
      case OVERLAY_NAMES.BUG_REPORT_NEW:
        return <BugReportNew />;
      case OVERLAY_NAMES.TASK_NEW_EDIT:
        return <TaskNewEdit />;
      case OVERLAY_NAMES.TASK_PROFILE:
        return <TaskProfile />;
      case OVERLAY_NAMES.TASKS_AUTOMATED:
        return <TasksAutomated />;
      case OVERLAY_NAMES.MEMBER_NEW_EDIT:
        return <MemberNewEdit />;
      case OVERLAY_NAMES.SETTINGS_PROFILE_EDIT:
        return <SettingsProfileEdit />;
      case OVERLAY_NAMES.SETTINGS_LANGUAGE_NEW_EDIT:
        return <LanguageNewEdit />;
      case OVERLAY_NAMES.SETTINGS_DIALECT_NEW_EDIT:
        return <DialectNewEdit />;
      case OVERLAY_NAMES.SETTINGS_EMAIL_TEMPLATE_NEW_EDIT:
        return <CommunicationTemplateNewEdit />;
      case OVERLAY_NAMES.SETTINGS_MILEAGE_RATE_NEW_EDIT:
        return <MileageRateNewEdit />;
      case OVERLAY_NAMES.SETTINGS_UNIT_NEW_EDIT:
        return <UnitNewEdit />;
      case OVERLAY_NAMES.SETTINGS_HOLIDAY_NEW_EDIT:
        return <HolidayNewEdit />;
      case OVERLAY_NAMES.SETTINGS_LINGUIST_REQUIREMENT_NEW_EDIT:
        return <LinguistRequirementNewEdit />;
      case OVERLAY_NAMES.SETTINGS_DOCUMENT_TEMPLATE_NEW_EDIT:
        return <DocumentTemplateNewEdit />;
      case OVERLAY_NAMES.SETTINGS_SERVICE_DOCUMENT_NEW_EDIT:
        return <ServiceDocumentNewEdit />;
      case OVERLAY_NAMES.SETTINGS_PAYMENT_METHOD_NEW_EDIT:
        return <PaymentMethodNewEdit />;
      case OVERLAY_NAMES.SETTINGS_SERVICE_TYPE_NEW_EDIT:
        return <ServiceTypeNewEdit />;
      case OVERLAY_NAMES.SETTINGS_SERVICE_COST_TYPE_NEW_EDIT:
        return <ServiceCostTypeNewEdit />;
      case OVERLAY_NAMES.SETTINGS_SERVICE_COST_TYPE_RULES_EDIT:
        return <ServiceCostTypeRulesEdit />;
      case OVERLAY_NAMES.SETTINGS_NOTIFICATION_NEW_EDIT:
        return <NotificationNewEdit />;
      case OVERLAY_NAMES.SETTINGS_TIMESHEETS_MANUAL_NEW:
        return <TimesheetsManualNewEdit />;
      case OVERLAY_NAMES.SETTINGS_TIMESHEETS_IMPORT:
        return <TimesheetsImport />;
      case OVERLAY_NAMES.SETTINGS_EXPENSES_CATEGORY_NEW_EDIT:
        return <ExpensesCategoryNewEdit />;
      case OVERLAY_NAMES.SETTINGS_EXPENSES_SUBCATEGORY_NEW_EDIT:
        return <ExpensesSubcategoryNewEdit />;
      case OVERLAY_NAMES.MANAGEMENT_EXPENSE_NEW_EDIT:
        return <ExpenseNewEdit />;
      case OVERLAY_NAMES.EXPENSE_PROFILE:
        return <ExpenseProfile />;
      case OVERLAY_NAMES.GLOBAL_SEARCH:
        return <GlobalSearch />;
      case OVERLAY_NAMES.REPORTS_INSIGHTS:
        return <AIInsightsViewOverlay />;

      case OVERLAY_NAMES.LINGUIST_ASSIGNMENT_FINALIZE:
        return <LinguistAssignmentFinalize />;

      default:
        return null;
    }
  };

  return (
    <WebSocketProvider user={currentUser}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <ThemeProvider theme={theme}>
            <CssBaseline />
            <div className="app">
              <Router>
                <PresenceProvider>
                <Routes>
                {/* Public Card Routes - Always accessible */}
                <Route path="/amer" element={<AmerCard />} />
                <Route path="/operations" element={<OperationsCard />} />

                {/* Conditional routing based on subdomain */}
                {isCards ? (
                  // Cards subdomain - only show card routes
                  <>
                    <Route path="/" element={<CardsRedirect />} />
                    <Route path="*" element={<Navigate to="/operations" replace />} />
                  </>
                ) : (
                  // Admin subdomain - show full portal
                  <>
                    {/* Authentication Routes */}
                    <Route path="/" element={<Login />} />
                    <Route path="/login" element={<Login />} />
                    <Route path="/loginConfirmation" element={<LoginConfirmation />} />
                    <Route path="/__/auth/action" element={<LoginConfirmation />} />
                    <Route path="/linguist-login" element={<LinguistLogin />} />
                    <Route
                      path="/linguist-portal"
                      element={(
                        <LinguistProtectedRoute>
                          <LinguistPortalLayout />
                        </LinguistProtectedRoute>
                      )}
                    >
                      <Route index element={<LinguistDashboard />} />
                      <Route path="interpretations" element={<LinguistInterpretations />} />
                      <Route path="profile" element={<LinguistProfile />} />
                      <Route path="notifications" element={<LinguistNotifications />} />
                      <Route path="assignments/:id" element={<LinguistAssignmentDetail />} />
                    </Route>
                    <Route path="/linguist-portal/setup-password" element={<LinguistInvitationSetup />} />

                <Route element={<Layout />}>
                  <Route path="/dashboard" element={<Dashboard />} />
                  <Route path="/services" element={<Services />} />
                  <Route path="/services/:id" element={<ServiceProfile />} />
                  <Route path="/interpretations" element={<Interpretations />} />
                  <Route path="/interpretations/:id" element={<InterpretationProfile />} />
                  <Route path="/translations" element={<Translations />} />
                  <Route path="/translations/:id" element={<TranslationProfile />} />
                  <Route path="/otherServices" element={<OtherServices />} />
                  <Route path="/otherServices/:id" element={<OtherServiceProfile />} />
                  <Route path="/clients" element={<Clients />} />
                  <Route path="/linguists" element={<Linguists />} />
                  <Route path="/linguists/:id" element={<LinguistsProfile />} />
                  <Route path="/clients/:id" element={<ClientProfile />} />
                  <Route
                    path="/invoices/:id"
                    element={
                      <ProtectedRoute allowedRoles={["Admin"]}>
                        <InvoiceProfile />
                      </ProtectedRoute>
                    }
                  />
                  <Route path="/operationsTools" element={<OperationsTools />} />
                  <Route path="/calendar" element={<Calendar />} />
                  <Route path="/tasks" element={<Tasks />} />
                  <Route path="/notifications" element={<Notifications />} />
                  <Route path="/help" element={<Help />} />
                  <Route
                    path="/expenses"
                    element={
                      <ProtectedRoute allowedRoles={["Admin"]}>
                        <Expenses />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/invoices"
                    element={
                      <ProtectedRoute allowedRoles={["Admin"]}>
                        <Invoices />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/linguistsPayments"
                    element={
                      <ProtectedRoute allowedRoles={["Admin"]}>
                        <LinguistsPayments />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/reports"
                    element={
                      <ProtectedRoute allowedRoles={["Admin"]}>
                        <Reports />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/members"
                    element={
                      <ProtectedRoute allowedRoles={["Admin"]}>
                        <Members />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/members/:id"
                    element={
                      <ProtectedRoute allowedRoles={["Admin"]}>
                        <MemberProfile />
                      </ProtectedRoute>
                    }
                  />
                  <Route path="/settings" element={<Settings />} />
                  <Route path="/bugReports" element={<BugReports />} />
                  <Route path="/bugReports/:id" element={<BugReportProfile />} />
                </Route>

                <Route path="/404" element={<NotFound />} />
                <Route path="*" element={<Navigate to="/404" replace />} />
                  </>
                )}
              </Routes>

              {!isCards && renderOverlay()}
              {!isCards && overlayData?.quickCreateLoading && (
                <Backdrop
                  open
                  sx={{
                    zIndex: (muiTheme) => muiTheme.zIndex.modal + 2,
                    color: theme.palette.primary.main,
                    backgroundColor: "rgba(0, 0, 0, 0.35)",
                  }}
                >
                  <Box
                    sx={{
                      minWidth: 280,
                      px: 3,
                      py: 2.5,
                      borderRadius: "14px",
                      backgroundColor: theme.palette.background.default,
                      boxShadow: theme.shadows[8],
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: 1.25,
                    }}
                  >
                    <CircularProgress size={28} />
                    <Typography variant="body1" fontWeight={700}>
                      {overlayData?.quickCreateLoadingLabel || "Loading form data..."}
                    </Typography>
                  </Box>
                </Backdrop>
              )}
              </PresenceProvider>
            </Router>
          </div>
        </ThemeProvider>
      </LocalizationProvider>
    </WebSocketProvider>
  );
}

export default App;
